// The text logic behind Free Type Feedback, kept apart from the component so it can be
// tested directly: finding finished sentences, holding back the one being edited,
// diffing a correction, recognising a fix of an earlier sentence, and wrapping a cloze.

// A sentence is a run of text up to and including terminal punctuation (plus any
// closing quotes or brackets). Spanish ¿¡ open a sentence, so they aren't terminators.
const SENTENCE_REGEX = /[^.!?。！？…\n]+[.!?。！？…]+["'»”’)\]]*/g;

// Finished sentences, each with its [start, end) offsets - start so the preceding text
// can go along as context, both so a sentence with the caret in it can be held back. The
// last line's unterminated tail is still being written; an earlier line's isn't -
// pressing Enter finishes a sentence too.
export function finishedSentences(text) {
  const results = [];
  let offset = 0;
  const lines = text.split("\n");

  const add = (raw, index) => {
    const sentence = raw.trim();
    const start = index + raw.length - raw.trimStart().length;
    results.push({ end: start + sentence.length, sentence, start });
  };

  lines.forEach((line, lineIndex) => {
    let consumed = 0;
    let match;
    SENTENCE_REGEX.lastIndex = 0;

    while ((match = SENTENCE_REGEX.exec(line)) !== null) {
      add(match[0], offset + match.index);
      consumed = match.index + match[0].length;
    }

    const tail = line.slice(consumed);
    if (lineIndex < lines.length - 1 && tail.trim()) {
      add(tail, offset + consumed);
    }

    offset += line.length + 1;
  });

  // A stray "a." or "!" isn't worth a round trip.
  return results.filter(({ sentence }) => sentence.replace(/[^\p{L}]/gu, "").length >= 2);
}

// Word-level diff (character-level for languages written without spaces), as
// [{ text, type: "same" | "removed" | "added" }]. Plain LCS: sentences are short.
export function diff(a, b) {
  const byChar = !/\s/.test(a) && !/\s/.test(b);
  const tokenize = (s) => (byChar ? Array.from(s) : s.split(/(\s+)/).filter(Boolean));
  const x = tokenize(a);
  const y = tokenize(b);
  const table = Array.from({ length: x.length + 1 }, () => new Array(y.length + 1).fill(0));

  for (let i = x.length - 1; i >= 0; i -= 1) {
    for (let j = y.length - 1; j >= 0; j -= 1) {
      table[i][j] = x[i] === y[j] ? table[i + 1][j + 1] + 1 : Math.max(table[i + 1][j], table[i][j + 1]);
    }
  }

  const parts = [];
  const push = (text, type) => {
    const last = parts[parts.length - 1];
    if (last && last.type === type) last.text += text;
    else parts.push({ text, type });
  };

  let i = 0;
  let j = 0;
  while (i < x.length && j < y.length) {
    if (x[i] === y[j]) {
      push(x[i], "same");
      i += 1;
      j += 1;
    } else if (table[i + 1][j] >= table[i][j + 1]) {
      push(x[i], "removed");
      i += 1;
    } else {
      push(y[j], "added");
      j += 1;
    }
  }
  while (i < x.length) push(x[i++], "removed");
  while (j < y.length) push(y[j++], "added");

  return parts;
}

// How much of two sentences is shared, 0-1: longest common subsequence of characters.
// Characters rather than words, because a fix is usually an accent or an ending -
// "allé a" to "allée à" shares almost every character but none of those words.
export function similarity(a, b) {
  const x = Array.from(a);
  const y = Array.from(b);
  let previous = new Array(y.length + 1).fill(0);

  x.forEach((char) => {
    const row = [0];
    y.forEach((other, j) => {
      row.push(char === other ? previous[j] + 1 : Math.max(previous[j + 1], row[j]));
    });
    previous = row;
  });

  return (2 * previous[y.length]) / (x.length + y.length);
}

// Below this, a new sentence is treated as new rather than as a fix of an old one.
// Unrelated sentences in the same language share a lot of letters and score 0.3-0.45;
// fixing an ending scores 0.9+, and fixing one while adding a clause about 0.7.
export const REVISION_SIMILARITY = 0.6;

// Whether the caret (or selection) is in this sentence, i.e. it's being edited. Checking
// a sentence mid-edit sends every intermediate version, so it waits until the caret
// leaves. The one exception is the caret sitting right after the last sentence in the
// text: that's the normal "just finished writing it" position, not an edit.
export function isEditing({ start, end }, selection, text) {
  if (!selection) return false;

  const appending = selection.start === selection.end && selection.start === end && end === text.trimEnd().length;
  return !appending && selection.start <= end && selection.end >= start;
}

// The sentence with the cloze word wrapped in {{ }}, which is what tells Quick Capture
// "this is already a sentence, just translate it" rather than "generate a sentence for
// this word". Whole-word match where the language has spaces, so "a" doesn't cloze the
// "a" inside "avait". null when the word can't be found.
export function wrapCloze(sentence, cloze) {
  if (!cloze || sentence.includes("{{")) return null;

  const escaped = cloze.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
  const pattern = /\s/.test(sentence) ? `(?<![\\p{L}\\p{M}])${escaped}(?![\\p{L}\\p{M}])` : escaped;
  const match = new RegExp(pattern, "u").exec(sentence);
  if (!match) return null;

  return `${sentence.slice(0, match.index)}{{${cloze}}}${sentence.slice(match.index + cloze.length)}`;
}

// Words for the counter - what a "write 250 words" task means. Languages written without
// spaces count characters instead (letters only, not punctuation or spaces).
export function countWords(text, noWordBoundaries = false) {
  if (noWordBoundaries) return (text.match(/\p{L}/gu) || []).length;

  return text.split(/\s+/).filter((word) => /[\p{L}\p{N}]/u.test(word)).length;
}
